import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { useAccountOwnerSelect } from '../AccountOwnerSelect/useAccountOwnerSelect';
import { selectCurrentClient } from '../../../../../../../../CommonSelectors/CommonAreaSelector';

interface Params {
  options: any[];
  type?: 'year' | 'age';
}

export const useYearAgeOptionsWithRightLabel = ({ options, type = 'year' }: Params) => {
  const formBag = useFormContext();

  const [ownerModel] = formBag.watch(['bankAccount.OwnerModel']);

  const { options: ownerOptions } = useAccountOwnerSelect();

  const isOwnerPrimary =
    ownerModel.length === 1 && ownerModel[0].Name === ownerOptions[0].options[0].value;
  const isOwnerSpouse =
    ownerModel.length === 1 && ownerModel[0].Name === ownerOptions[0].options[1]?.value;

  const isOwnerJoint = ownerModel.length > 1 || ownerModel.some(owner => owner.Name === 'Joint');

  const { Dob: primaryDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const getYearFromAge = (yearOfBirth: number | null, age: number | null) =>
    yearOfBirth && age ? `Year ${yearOfBirth + age}` : null;

  const getAgeFromYear = (yearOfBirth: number | null, year: number | null) =>
    yearOfBirth && year ? `Age ${year - yearOfBirth}` : null;

  const getRightLabel = (year: number | null, age: number | null) => {
    const ownerConfigs = {
      primary: isOwnerPrimary && primaryYob,
      spouse: isOwnerSpouse && spouseYob,
      joint: isOwnerJoint && [
        { condition: !!primaryYob, yob: primaryYob },
        { condition: !!spouseYob, yob: spouseYob },
      ],
    };

    if (type === 'age' && age) {
      if (ownerConfigs.primary) {
        return getYearFromAge(primaryYob, age);
      }

      if (ownerConfigs.spouse) {
        return getYearFromAge(spouseYob, age);
      }

      if (ownerConfigs.joint) {
        return ownerConfigs.joint
          .filter(({ condition }) => condition)
          .map(({ yob }) => getYearFromAge(yob, age))
          .filter(Boolean)
          .join(', ');
      }
    }

    if (type === 'year' && year) {
      if (ownerConfigs.primary) {
        return getAgeFromYear(primaryYob, year);
      }

      if (ownerConfigs.spouse) {
        return getAgeFromYear(spouseYob, year);
      }

      if (ownerConfigs.joint) {
        return ownerConfigs.joint
          .filter(({ condition }) => condition)
          .map(({ yob }) => getAgeFromYear(yob, year))
          .filter(Boolean)
          .join(', ');
      }
    }

    return undefined;
  };

  return options.map(option => {
    const year = type === 'year' ? parseInt(option.label, 10) || null : null;
    const age = type === 'age' ? parseInt(option.label, 10) || null : null;

    if ((type === 'year' && !year) || (type === 'age' && !age)) {
      return option;
    }

    return {
      ...option,
      rightLabel: getRightLabel(year, age),
    };
  });
};
